import { SurvivalSaveData, isValidSurvivalSaveData } from '../types/SurvivalSaveData';
import {
  createShuffledOpponentQueue,
  calcAutoGrowth,
  SURVIVAL_BONUS_POINTS_PER_WIN,
  STAT_CAP,
} from '../constants/survivalConfig';

export const SURVIVAL_STORAGE_KEY = 'monster-buttle-survival';
export const SURVIVAL_BEST_KEY = 'monster-buttle-survival-best';

/**
 * サバイバルセーブデータをLocalStorageからロードする
 */
export function loadSurvivalSave(): SurvivalSaveData | null {
  try {
    const raw = localStorage.getItem(SURVIVAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isValidSurvivalSaveData(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * サバイバルセーブデータをLocalStorageに保存する
 */
export function saveSurvivalData(data: SurvivalSaveData): void {
  localStorage.setItem(SURVIVAL_STORAGE_KEY, JSON.stringify(data));
}

/**
 * サバイバルセーブデータをLocalStorageから削除する
 */
export function deleteSurvivalSave(): void {
  localStorage.removeItem(SURVIVAL_STORAGE_KEY);
}

/**
 * 勝利を記録してセーブデータを更新する（immutable）
 * - 自動成長をステータスに適用（インクリメント前のwinCountでフェーズ計算）
 * - bonusPoints を +SURVIVAL_BONUS_POINTS_PER_WIN
 * - winCount を +1
 * - opponentQueue の先頭を消費（空になったら次の周を生成）
 */
export function recordSurvivalWin(data: SurvivalSaveData): SurvivalSaveData {
  const growth = calcAutoGrowth(data.monsterId, data.winCount);
  const cap = (val: number) => Math.min(val, STAT_CAP);
  const newStats = {
    hp: cap(data.stats.hp + growth.hp),
    strength: cap(data.stats.strength + growth.strength),
    special: cap(data.stats.special + growth.special),
    speed: cap(data.stats.speed + growth.speed),
    toughness: cap(data.stats.toughness + growth.toughness),
  };

  const newQueue = data.opponentQueue.slice(1);
  const nextQueue =
    newQueue.length > 0 ? newQueue : createShuffledOpponentQueue(data.monsterId, []);
  return {
    ...data,
    winCount: data.winCount + 1,
    bonusPoints: data.bonusPoints + SURVIVAL_BONUS_POINTS_PER_WIN,
    stats: newStats,
    opponentQueue: nextQueue,
  };
}

function loadBestMap(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SURVIVAL_BEST_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return parsed as Record<string, number>;
  } catch {
    return {};
  }
}

/**
 * キャラ別の最高連勝記録をロードする（記録なしは0）
 */
export function loadSurvivalBest(monsterId: string): number {
  const map = loadBestMap();
  const val = map[monsterId];
  return typeof val === 'number' && !isNaN(val) ? val : 0;
}

/**
 * キャラ別の最高連勝記録を更新する（より大きい値のみ）
 */
export function updateSurvivalBest(monsterId: string, winCount: number): void {
  if (winCount <= loadSurvivalBest(monsterId)) return;
  const map = loadBestMap();
  map[monsterId] = winCount;
  localStorage.setItem(SURVIVAL_BEST_KEY, JSON.stringify(map));
}
